import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Building, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import PhoneInput from "../ui/PhoneInput";

type AudienceType = "high-school" | "university" | "executive";

interface ApiResponse {
    success?: boolean;
    message?: string;
    errors?: { [key: string]: string | string[] };
}

export interface BrochureLeadModalProps {
    isOpen: boolean;
    onClose: () => void;
    brochureUrl: string;
    downloadFilename: string;
    source: string;
    title?: string;
    subtitle?: string;
}

const BrochureLeadModal: React.FC<BrochureLeadModalProps> = ({
    isOpen,
    onClose,
    brochureUrl,
    downloadFilename,
    source,
    title = "Access the Elite Workshop Series Brochure",
    subtitle = "Enter your details to download the full guide. We'll save your information and be in touch.",
}) => {
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        institution: "",
        audience: "high-school" as AudienceType,
        consent: false,
    });
    const [formErrors, setFormErrors] = useState<{ [k: string]: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [brochureDownloaded, setBrochureDownloaded] = useState(false);

    const mapAudienceToRole = (audience: AudienceType): string => {
        const roleMapping: Record<AudienceType, string> = {
            "high-school": "High school educator / coordinator",
            "university": "University staff / student",
            "executive": "Executive / HR / L&D",
        };
        return roleMapping[audience];
    };

    const validateForm = () => {
        const errors: { [k: string]: string } = {};
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

        if (!formData.firstName.trim()) {
            errors.firstName = "First name is required";
        } else if (formData.firstName.trim().length < 2) {
            errors.firstName = "First name must be at least 2 characters";
        } else if (!/^[a-zA-Z\s'-]+$/.test(formData.firstName.trim())) {
            errors.firstName = "First name can only contain letters, spaces, hyphens, and apostrophes";
        }

        if (!formData.lastName.trim()) {
            errors.lastName = "Last name is required";
        } else if (formData.lastName.trim().length < 2) {
            errors.lastName = "Last name must be at least 2 characters";
        } else if (!/^[a-zA-Z\s'-]+$/.test(formData.lastName.trim())) {
            errors.lastName = "Last name can only contain letters, spaces, hyphens, and apostrophes";
        }

        if (!formData.email.trim()) {
            errors.email = "Email address is required";
        } else if (!emailRegex.test(formData.email.trim())) {
            errors.email = "Please enter a valid email address (e.g., user@example.com)";
        }

        if (!formData.consent) errors.consent = "Please agree to be contacted";

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const triggerBrochureDownload = () => {
        const link = document.createElement("a");
        link.href = brochureUrl;
        link.download = downloadFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        setFormErrors({});

        const apiPayload = {
            first_name: formData.firstName.trim(),
            last_name: formData.lastName.trim(),
            email: formData.email.trim().toLowerCase(),
            phone: formData.phone.trim() || null,
            institution: formData.institution.trim() || null,
            role: mapAudienceToRole(formData.audience),
        };

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
        const apiUrl = `${API_BASE_URL}/marketing-contacts`;

        if (typeof window !== "undefined" && window.location?.hostname === "localhost") {
            console.log("[Brochure form] POST", apiUrl);
            console.log("[Brochure form] Payload", apiPayload);
        }

        try {
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify(apiPayload),
            });

            let result: ApiResponse = {};
            try {
                const text = await response.text();
                result = text ? (JSON.parse(text) as ApiResponse) : {};
            } catch {
                if (typeof window !== "undefined") console.error("[Brochure form] Invalid JSON response", response.status);
                setFormErrors({ submit: "Invalid response from server. Please try again." });
                triggerBrochureDownload();
                return;
            }

            if (typeof window !== "undefined" && window.location?.hostname === "localhost") {
                console.log("[Brochure form] Response", response.status, result);
            }

            if (!response.ok) {
                if (result.errors) {
                    const backendErrors: { [k: string]: string } = {};
                    Object.keys(result.errors).forEach(key => {
                        const errorValue = result.errors![key];
                        const message = Array.isArray(errorValue) ? errorValue[0] : errorValue;
                        const frontendKey = key === "first_name" ? "firstName" : key === "last_name" ? "lastName" : key;
                        backendErrors[frontendKey] = message;
                    });
                    setFormErrors(backendErrors);
                } else {
                    setFormErrors({ submit: result.message || `Server error: ${response.status}` });
                }
                triggerBrochureDownload();
                return;
            }

            const leadPayload = { ...formData, source, createdAt: new Date().toISOString(), submittedToBackend: true };
            localStorage.setItem("nyea_brochure_lead", JSON.stringify(leadPayload));

            setBrochureDownloaded(true);
            triggerBrochureDownload();

            setTimeout(() => {
                onClose();
                setFormData({ firstName: "", lastName: "", email: "", phone: "", institution: "", audience: "high-school" as AudienceType, consent: false });
                setFormErrors({});
                setBrochureDownloaded(false);
            }, 1500);
        } catch (error: unknown) {
            const err = error as { message?: string; name?: string };
            if (typeof window !== "undefined") console.error("[Brochure form] Error", err);
            let errorMessage = "Unable to submit form. Please try again or contact us directly.";
            if (err?.message) errorMessage = err.message;
            else if (err?.name === "TypeError") errorMessage = "Cannot connect to server. Please check if backend is running and CORS is allowed.";
            setFormErrors({ submit: errorMessage });
            triggerBrochureDownload();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => !isSubmitting && onClose()}
                >
                    <motion.div
                        className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden"
                        initial={{ scale: 0.95, y: 12, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.95, y: 12, opacity: 0 }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="p-6 border-b">
                            <h4 className="text-xl font-bold">{title}</h4>
                            <p className="text-gray-600 mt-1">{subtitle}</p>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-3">
                            {/* First Name */}
                            <div>
                                <label htmlFor="brochure-firstName" className="block text-sm font-medium text-gray-700 mb-1">
                                    First Name *
                                </label>
                                <input
                                    type="text"
                                    id="brochure-firstName"
                                    value={formData.firstName}
                                    onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 ${formErrors.firstName ? "border-red-300" : "border-gray-300"}`}
                                    placeholder="Enter your first name"
                                    disabled={isSubmitting}
                                />
                                {formErrors.firstName && <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>}
                            </div>

                            {/* Last Name */}
                            <div>
                                <label htmlFor="brochure-lastName" className="block text-sm font-medium text-gray-700 mb-1">
                                    Last Name *
                                </label>
                                <input
                                    type="text"
                                    id="brochure-lastName"
                                    value={formData.lastName}
                                    onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 ${formErrors.lastName ? "border-red-300" : "border-gray-300"}`}
                                    placeholder="Enter your last name"
                                    disabled={isSubmitting}
                                />
                                {formErrors.lastName && <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>}
                            </div>

                            {/* Email Address */}
                            <div>
                                <label htmlFor="brochure-email" className="block text-sm font-medium text-gray-700 mb-1">
                                    Email Address *
                                </label>
                                <input
                                    type="email"
                                    id="brochure-email"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 ${formErrors.email ? "border-red-300" : "border-gray-300"}`}
                                    placeholder="Enter your email address"
                                    disabled={isSubmitting}
                                />
                                {formErrors.email && <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>}
                            </div>

                            {/* Phone Number (optional) */}
                            <div>
                                <label htmlFor="brochure-phone" className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone Number <span className="text-gray-500 font-normal">(optional)</span>
                                </label>
                                <PhoneInput
                                    value={formData.phone}
                                    onChange={value => setFormData({ ...formData, phone: value })}
                                    error={formErrors.phone}
                                    disabled={isSubmitting}
                                    placeholder="Enter your phone number"
                                />
                                {formErrors.phone && <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>}
                            </div>

                            {/* Institution (optional) - same as old form */}
                            <div>
                                <label htmlFor="brochure-institution" className="block text-sm font-medium text-gray-700 mb-1">
                                    <Building className="inline h-4 w-4 mr-1" />
                                    Institution <span className="text-gray-500 font-normal">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    id="brochure-institution"
                                    value={formData.institution}
                                    onChange={e => setFormData({ ...formData, institution: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
                                    placeholder="School / University / Company"
                                    disabled={isSubmitting}
                                />
                            </div>

                            {/* I am a - same as old form */}
                            <div>
                                <label htmlFor="brochure-audience" className="block text-sm font-medium text-gray-700 mb-1">
                                    I am a
                                </label>
                                <select
                                    id="brochure-audience"
                                    value={formData.audience}
                                    onChange={e => setFormData({ ...formData, audience: e.target.value as AudienceType })}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 ${formErrors.audience ? "border-red-300" : "border-gray-300"}`}
                                    disabled={isSubmitting}
                                >
                                    <option value="high-school">High school educator / coordinator</option>
                                    <option value="university">University staff / student</option>
                                    <option value="executive">Executive / HR / L&D</option>
                                </select>
                            </div>

                            {/* Consent - same as old form */}
                            <label className="inline-flex items-center">
                                <input
                                    type="checkbox"
                                    checked={formData.consent}
                                    onChange={e => setFormData({ ...formData, consent: e.target.checked })}
                                    className="mr-2 rounded border-gray-300"
                                    disabled={isSubmitting}
                                />
                                <span className="text-sm text-gray-700">I agree to be contacted about this brochure and related programs.</span>
                            </label>
                            {formErrors.consent && <p className="text-sm text-red-600">{formErrors.consent}</p>}
                            {formErrors.submit && (
                                <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                                    {formErrors.submit}
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4">
                                <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting} className="w-full sm:flex-1">
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={isSubmitting} className="w-full sm:flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0">
                                    {isSubmitting ? "Downloading…" : "Download brochure"}
                                </Button>
                            </div>
                            {brochureDownloaded && (
                                <div className="flex items-center gap-2 text-green-700 text-sm mt-2 bg-green-50 p-3 rounded-lg">
                                    <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                                    <span>Success! Brochure is downloading. We'll contact you shortly.</span>
                                </div>
                            )}
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default BrochureLeadModal;
